import cx from 'classnames';
import uniqid from 'uniqid';
import PropTypes from 'prop-types';
import { getCloudinaryUrl } from '../../../utils/imageUtils';

import './priceSpec.scss';

const PriceSpec = ({
  hotel,
  transportInfo,
  searchInformation,
  labels,
  imageDomain,
}) => {
  const getPriceTableRow = (label, multiplier, cost, total, isRebate, id) => {
    const cellClasses = cx({
      'hotel-price-spec__price-table-cell': true,
      'hotel-price-spec__price-table-cell--pink': isRebate,
    });

    const pricePrefix = isRebate ? '-' : '';

    return (
      <div className="hotel-price-spec__price-table-row" key={id}>
        <div className="hotel-price-spec__price-table-cell">{label}:</div>
        <div className="hotel-price-spec__price-table-cell">{multiplier} x</div>
        <div className={cellClasses}>
          {pricePrefix}
          {cost}
        </div>
        <div className={cellClasses}>=</div>
        <div className={cellClasses}>
          {pricePrefix}
          {total}
        </div>
      </div>
    );
  };

  const getPassengerGroupLabel = (age) => {
    if (age === -1) {
      return labels.price_adultLabel;
    }
    return `${labels.price_childLabel} (${age} ${labels.price_yearLabel})`;
  };

  const getPassengerGroups = () => {
    const priceGroupsRows = [];
    hotel.priceBreakDown.passengerPriceGroups.forEach((group, index) => {
      const label = `${labels.price_basePriceLabel} ${getPassengerGroupLabel(
        group.age,
      )}`;
      priceGroupsRows.push(
        getPriceTableRow(
          label,
          group.numberOfPassengers,
          group.basePrice,
          group.basePriceTotal,
          false,
          index,
        ),
      );
    });
    return priceGroupsRows;
  };

  const getPassengerRebates = () => {
    const priceGroupsRebates = [];
    hotel.priceBreakDown.passengerPriceGroups.forEach((group, index) => {
      if (group.discount) {
        const label = `${labels.price_discountLabel} ${getPassengerGroupLabel(
          group.age,
        )}`;
        priceGroupsRebates.push(
          getPriceTableRow(
            label,
            group.numberOfPassengers,
            group.discount,
            group.discountTotal,
            true,
            index,
          ),
        );
      }
    });
    return priceGroupsRebates;
  };

  const getInternetRebates = () => {
    const internetRebates = [];
    hotel.priceBreakDown.internetRebates?.forEach((rebate, index) => {
      internetRebates.push(
        getPriceTableRow(
          rebate.name,
          '1',
          rebate.amount,
          rebate.amount,
          true,
          index,
        ),
      );
    });
    return internetRebates;
  };

  const getSpecialOffer = () => {
    if (hotel.priceBreakDown.specialOffer) {
      const discount = hotel.priceBreakDown.specialOffer.formattedDiscount;
      return getPriceTableRow(
        `${hotel.priceBreakDown.specialOffer.text} ${labels.price_specialOfferLabel}`,
        1,
        discount,
        discount,
        true,
      );
    }
    return null;
  };

  const getEuTax = () => {
    const { euTax } = hotel.priceBreakDown;
    if (euTax !== '' && euTax != null) {
      return getPriceTableRow(labels.price_euTaxLabel, 1, euTax, euTax, false);
    }
    return null;
  };

  const getFlightTax = () => {
    return getPriceTableRow(
      labels.price_flightTax,
      1,
      hotel.priceBreakDown.flightTax,
      hotel.priceBreakDown.flightTax,
      false,
    );
  };

  const getTaxAndFeesIncludedInPrice = () => {
    const { taxAndFeesIncludedInPrice } = hotel.priceBreakDown;

    if (
      taxAndFeesIncludedInPrice === undefined ||
      taxAndFeesIncludedInPrice === null
    )
      return null;
    const rows = taxAndFeesIncludedInPrice.map((taxAndFee, index) => {
      const labelElement = `${taxAndFee.description} ${taxAndFee.additionalText}`;
      return getPriceTableRow(
        labelElement,
        1,
        taxAndFee.price,
        taxAndFee.price,
        false,
        index,
      );
    });

    return rows;
  };

  const getTaxTableRow = (label, total, id) => {
    return (
      <div className="hotel-price-spec__tax-table-row" key={id}>
        <div className="hotel-price-spec__tax-table-cell">{label}:</div>
        <div className="hotel-price-spec__tax-table-cell">{total}</div>
      </div>
    );
  };

  const getTaxAndFeesPayAtDestination = () => {
    const { taxAndFeesPayAtDestination } = hotel.priceBreakDown;

    if (
      taxAndFeesPayAtDestination === undefined ||
      taxAndFeesPayAtDestination === null
    )
      return null;
    if (taxAndFeesPayAtDestination.taxAndFees === null) return null;

    const rows = taxAndFeesPayAtDestination.taxAndFees.map(
      (taxAndFee, index) => {
        const labelElement = `${taxAndFee.description}`;
        return getTaxTableRow(labelElement, taxAndFee.price, index);
      },
    );

    return (
      <div className="hotel-price-spec__tax-table">
        <div className="hotel-price-spec__tax-table-title">
          {taxAndFeesPayAtDestination.title}:
        </div>
        {rows}
      </div>
    );
  };

  const mealsText = hotel.boardCodeDescription
    ? hotel.boardCodeDescription
    : labels.price_noMealsLabel;

  return (
    <div className="hotel-price-spec">
      <div className="hotel-price-spec__image-container">
        <div className="hotel-price-spec__image">
          <img
            src={getCloudinaryUrl(
              imageDomain,
              hotel?.hotelImage?.path,
              215,
              380,
            )}
            alt="hotel"
          />
        </div>
      </div>
      <div className="hotel-price-spec__info">
        <h3>{hotel.name}</h3>
        <div className="hotel-price-spec__info-row">
          <div className="hotel-price-spec__info-col">
            {labels.price_passengerLabel}:
          </div>
          <div className="hotel-price-spec__info-col">
            {searchInformation.passengerInfo}
          </div>
        </div>
        <div className="hotel-price-spec__info-row">
          <div className="hotel-price-spec__info-col">
            {labels.price_roomTypeLabel}:
          </div>
          <div className="hotel-price-spec__info-col">
            {hotel.roomDescription.map((room, index) => {
              return (
                <p
                  key={uniqid(`${index}-${room.description}-`)}
                >{`${room.description} (${room.count} ${labels.price_roomCountSuffix})`}</p>
              );
            })}
            {hotel.amenitiesDescription && <p>{hotel.amenitiesDescription}</p>}
          </div>
        </div>
        <div className="hotel-price-spec__info-row">
          <div className="hotel-price-spec__info-col">{labels.price_meals}:</div>
          <div className="hotel-price-spec__info-col">{mealsText}</div>
        </div>
        <div className="hotel-price-spec__info-row">
          <div className="hotel-price-spec__info-col">
            {labels.price_departureAirportLabel}:
          </div>
          <div className="hotel-price-spec__info-col">
            {transportInfo.outBoundFlights[0].departureLocation}
          </div>
        </div>
        <div className="hotel-price-spec__info-row">
          <div className="hotel-price-spec__info-col">
            {labels.price_departureLabel}:
          </div>
          <div className="hotel-price-spec__info-col">
            {transportInfo.outBoundFlights[0].departureDate},{' '}
            {labels.price_timePrefix}{' '}
            {transportInfo.outBoundFlights[0].departureTime}
          </div>
        </div>
        <div className="hotel-price-spec__info-row">
          <div className="hotel-price-spec__info-col">
            {labels.price_tripDurationLabel}:
          </div>
          <div className="hotel-price-spec__info-col">
            {searchInformation.durationInfo}
          </div>
        </div>
      </div>
      <div className="hotel-price-spec__clear" />
      <div className="hotel-price-spec__price-table">
        {getPassengerGroups()}
        {getFlightTax()}
        {getEuTax()}
        {getPassengerRebates()}
        {getInternetRebates()}
        {getSpecialOffer()}
        {getTaxAndFeesIncludedInPrice()}
      </div>
      <div className="hotel-price-spec__total-price-label">
        {labels.price_totalLabel}
      </div>
      <div className="hotel-price-spec__total-price">
        {hotel.priceBreakDown.totalPrice}
      </div>
      <div className="hotel-price-spec__clear" />
      {getTaxAndFeesPayAtDestination()}
    </div>
  );
};

PriceSpec.propTypes = {
  hotel: PropTypes.shape({
    amenitiesDescription: PropTypes.string,
    boardCodeDescription: PropTypes.string,
    hotelImage: PropTypes.shape({
      path: PropTypes.string,
    }),
    name: PropTypes.string,
    priceBreakDown: PropTypes.shape({
      euTax: PropTypes.string,
      flightTax: PropTypes.string,
      internetRebates: PropTypes.arrayOf(PropTypes.shape()),
      passengerPriceGroups: PropTypes.arrayOf(PropTypes.shape()),
      specialOffer: PropTypes.shape({
        formattedDiscount: PropTypes.string,
        text: PropTypes.string,
      }),
      taxAndFeesIncludedInPrice: PropTypes.arrayOf(PropTypes.shape()),
      taxAndFeesPayAtDestination: PropTypes.shape({
        taxAndFees: PropTypes.arrayOf(PropTypes.shape()),
        title: PropTypes.string,
      }),
      totalPrice: PropTypes.string,
    }),
    roomDescription: PropTypes.arrayOf(PropTypes.shape()),
  }).isRequired,
  transportInfo: PropTypes.shape().isRequired,
  searchInformation: PropTypes.shape().isRequired,
  labels: PropTypes.shape().isRequired,
  imageDomain: PropTypes.string.isRequired,
};

export default PriceSpec;
